import { useTranslation } from 'react-i18next';
import { Button, Chip } from '@haltongroup/halton-ui-library';
import { PageType } from '../types';
import { useSettings } from '../contexts/SettingsContext';
import { getNextJobNumber } from '../utils';
import AirflowPattern from './AirflowPattern';

interface LandingPageProps {
  onNavigate: (page: PageType) => void;
  savedOffersCount: number;
  savedWorkOrdersCount: number;
}

interface FeatureCardProps {
  className: string;
  icon: string;
  title: string;
  description: string;
  action: string;
  onClick: () => void;
  beta?: boolean;
}

const FeatureCard = ({ className, icon, title, description, action, onClick, beta }: FeatureCardProps) => (
  <div className={`feature-card ${className}`} onClick={onClick} role="button" tabIndex={0}
    onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') onClick(); }}>
    <div className="feature-card-body">
      {beta && <Chip label="BETA" theme="warning" priorityHigh className="feature-chip" />}
      <div className="feature-icon">
        <span className="material-symbols-outlined">{icon}</span>
      </div>
      <h3 className="feature-title">{title}</h3>
      <p className="feature-description">{description}</p>
      <div className="feature-action">
        <Button theme="primary" label={action} icon="arrow-right" iconPosition="end" className="btn-lg" />
      </div>
    </div>
  </div>
);

const LandingPage = ({ onNavigate, savedOffersCount, savedWorkOrdersCount }: LandingPageProps) => {
  const { t } = useTranslation();
  const { settings } = useSettings();
  const nextJobNumber = getNextJobNumber(settings.userIdentityNumber);

  return (
    <div className="landing-page">
      {/* Hero Section with Halton Brand Elements */}
      <div className="landing-hero">
        <AirflowPattern variant="light" />
        <div className="hero-content">
          <h1>{t('landing.welcome')}</h1>
          <p>{t('landing.subtitle')}</p>
        </div>
        <div className="hero-gradient-overlay" />
      </div>

      {/* Feature Cards Grid */}
      <section className="feature-section">
        <div className="feature-grid">
          <FeatureCard
            className="feature-card-calculator"
            icon="calculate"
            title={t('landing.calculatorCard.title')}
            description={t('landing.calculatorCard.description')}
            action={t('landing.calculatorCard.action')}
            onClick={() => onNavigate('calculator')}
          />
          <FeatureCard
            className="feature-card-workorder"
            icon="assignment"
            title={t('landing.workOrderCard.title')}
            description={t('landing.workOrderCard.description')}
            action={t('landing.workOrderCard.action')}
            onClick={() => onNavigate('workorder')}
          />
          <FeatureCard
            className="feature-card-maintenance"
            icon="build"
            title={t('landing.maintenanceCard.title')}
            description={t('landing.maintenanceCard.description')}
            action={t('landing.maintenanceCard.action')}
            onClick={() => onNavigate('maintenance')}
            beta
          />
          <FeatureCard
            className="feature-card-secondary"
            icon="inventory_2"
            title={t('landing.archiveCard.title')}
            description={t('landing.archiveCard.description')}
            action={t('landing.archiveCard.action')}
            onClick={() => onNavigate('archive')}
          />
        </div>
      </section>

      {/* Quick Stats Section */}
      <div className="quick-stats">
        <AirflowPattern variant="light" />
        <div className="quick-stats-content">
          <div className="stat-card">
            <div className="stat-title">{t('landing.quickStats.quotes')}</div>
            <span className="stat-value">{savedOffersCount}</span>
          </div>
          <div className="stat-card">
            <div className="stat-title">{t('landing.quickStats.workOrders')}</div>
            <span className="stat-value">{savedWorkOrdersCount}</span>
          </div>
          <div className="stat-card">
            <div className="stat-title">{t('landing.quickStats.nextJobNumber')}</div>
            <span className="stat-value stat-value-job">{nextJobNumber}</span>
          </div>
        </div>
      </div>
    </div>
  );
};

export default LandingPage;
